import React, { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import U2000InputDecimal from "../../../u2000Core/components/formControls/input/U2000InputDecimal";
import U2000InputCurrency from "../../../u2000Core/components/formControls/input/U2000InputCurrency";
import U2000Label from "../../../u2000Core/components/formControls/label/U2000Label";
import { formatCurrency, type CurrencyFormatOptions } from "../../../u2000Core/tools/U2000CurrencyUtils";
import { u2000TimesheetCombinedHonorariumHours } from "../../../u2000Core/tools/U2000TimesheetFormDerivedTotals";
import { U3129_PROCESSED_TIMESHEET_STATUSES, type U3129Timesheet } from "../../U3129Service";
import type { U2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { NS } from "../../U3129";
import { useU3129Context } from "../../U3129Context";
import { u3129HideExpenseAndEquipmentTabs } from "../../tools/u3129HideExpenseAndEquipmentTabs";

interface U3129TimesheetFormHeaderTabProps {
  formStateHandlers: U2000FormStateHandlers<U3129Timesheet>;
}

/** Libellés et valeurs alignés à droite (champs numériques de l’en-tête). */
const U3129_HEADER_NUMERIC_FIELD_WRAP = "w-32 text-right [&_input]:text-right [&_.header-honorarium-sum-display]:text-right";

const HEADER_HONORARIUM_SUM_DISPLAY_FORMAT: CurrencyFormatOptions = {
  currencySymbol: "",
  symbolPosition: "left",
  decimalSeparator: ".",
  thousandSeparator: " ",
  allowNegative: true,
  showNegativeInParentheses: false,
  decimalDigits: 2,
};

function toFiniteNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

/** BL-0632 : panneau Sommaire replié par défaut pour libérer l'espace aux onglets de saisie. */
const U3129_TIMESHEET_SUMMARY_EXPANDED_BY_DEFAULT = false;

const U3129TimesheetFormHeaderTab: React.FC<U3129TimesheetFormHeaderTabProps> = ({ formStateHandlers }) => {
  const { t } = useTranslation();
  const { initLU } = useU3129Context();
  const hideExpenseAndEquipmentTabs = u3129HideExpenseAndEquipmentTabs(initLU.personalization);
  const [openEmployeePeriod, setOpenEmployeePeriod] = useState(U3129_TIMESHEET_SUMMARY_EXPANDED_BY_DEFAULT);
  const headerHonorariumSumId = useId();

  const data = formStateHandlers.getData();
  const showPayrollHoursBlock = data?.status != null && (U3129_PROCESSED_TIMESHEET_STATUSES as readonly string[]).includes(data.status);

  const combinedHonorariumHours = useMemo(
    () =>
      u2000TimesheetCombinedHonorariumHours({
        totalPaidHours: toFiniteNumber(data?.totalPaidHours),
        totalLeaveHours: toFiniteNumber(data?.totalLeaveHours),
        totalSpecialNet: toFiniteNumber(data?.totalSpecialNet),
      }),
    [data?.totalPaidHours, data?.totalLeaveHours, data?.totalSpecialNet],
  );

  /** Encadré rouge sur « Total honoraires » uniquement lorsque projet + abs./bnc. est strictement sous la base horaire. */
  const highlightCombinedProjectAndLeaveUnderBase = useMemo(() => {
    const baseRaw = data?.employee?.hourlyBase;
    const baseNum = baseRaw === null || baseRaw === undefined ? NaN : Number(baseRaw);
    if (!Number.isFinite(baseNum) || Math.abs(baseNum) < 0.0001) {
      return false;
    }
    return combinedHonorariumHours < baseNum;
  }, [data?.employee?.hourlyBase, combinedHonorariumHours]);

  const summaryToggleLabel = t("lblEmployeeAndPeriod", { ns: NS, defaultValue: "Sommaire" });

  if (!openEmployeePeriod) {
    return (
      <div className="flex min-h-0 shrink-0 items-center gap-3">
        <button
          type="button"
          aria-expanded={false}
          onClick={() => setOpenEmployeePeriod(true)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-sm py-0.5 pr-1 text-sm font-semibold text-gray-600 select-none hover:bg-gray-50 hover:text-gray-900 focus:outline-hidden focus:ring-2 focus:ring-blue-500/20"
        >
          {summaryToggleLabel}
          <FaChevronDown className="h-3 w-3" aria-hidden />
        </button>
        <div className="h-px min-w-0 flex-1 bg-gray-200" aria-hidden />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="relative mt-2 rounded-lg border border-gray-200 bg-white p-4 pt-6 shadow-md transition-shadow duration-200">
        <button
          type="button"
          aria-expanded={true}
          onClick={() => setOpenEmployeePeriod(false)}
          className="absolute -top-3 left-4 flex items-center gap-2 bg-white px-3 py-1 text-left text-sm font-semibold text-gray-700 shadow-xs border border-gray-200 rounded-md select-none hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-blue-500/20"
          style={{ zIndex: 1 }}
        >
          {summaryToggleLabel}
          <FaChevronUp className="w-3 h-3" aria-hidden />
        </button>
        <div className="flex flex-col gap-4 pt-2 xl:flex-row xl:flex-nowrap xl:items-start xl:gap-x-4 xl:overflow-x-auto xl:pb-0.5">
            <div className="shrink-0 xl:w-44 xl:flex-none text-right [&_input]:text-right">
              <U2000InputDecimal formStateHandlers={formStateHandlers} fieldName="employee.hourlyBase" label={t("lblHourlyBase", { ns: NS })} readonly allowNegative={false} decimalDigits={2} />
            </div>
            <div className="hidden shrink-0 xl:block xl:w-px xl:self-stretch xl:bg-gray-200" aria-hidden />
            <div className="flex w-max min-w-0 shrink-0 flex-nowrap gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-1 xl:flex-none">
              <div className={U3129_HEADER_NUMERIC_FIELD_WRAP}>
                <U2000InputDecimal
                  formStateHandlers={formStateHandlers}
                  fieldName="totalPaidHours"
                  label={t("lblTotalHonoraria", { ns: NS, defaultValue: "Total projet" })}
                  readonly
                  allowNegative={false}
                  decimalDigits={2}
                />
              </div>
              <div className={U3129_HEADER_NUMERIC_FIELD_WRAP}>
                <U2000InputDecimal
                  formStateHandlers={formStateHandlers}
                  fieldName="totalLeaveHours"
                  label={t("lblHeaderTotalAbsenceBanques", { ns: NS, defaultValue: "Total abs." })}
                  readonly
                  allowNegative={false}
                  decimalDigits={2}
                />
              </div>
              <div className={U3129_HEADER_NUMERIC_FIELD_WRAP}>
                <U2000InputDecimal
                  formStateHandlers={formStateHandlers}
                  fieldName="totalSpecialWithdraw"
                  label={t("lblHeaderTotalSpecialBankWithdraw", { ns: NS, defaultValue: "Tot ret. bnc." })}
                  readonly
                  allowNegative={false}
                  decimalDigits={2}
                />
              </div>
              <div className={U3129_HEADER_NUMERIC_FIELD_WRAP}>
                <div className="space-y-1.5">
                  <U2000Label
                    label={t("lblHeaderTotalSpecialBankDeposit", { ns: NS, defaultValue: "Tot dép. bnc." })}
                    required={false}
                  />
                  <div className="header-honorarium-sum-display w-full rounded-lg border border-gray-200 bg-muted px-4 py-3 text-sm tabular-nums text-gray-500">
                    {formatCurrency(-toFiniteNumber(data?.totalSpecialDeposit), HEADER_HONORARIUM_SUM_DISPLAY_FORMAT)}
                  </div>
                </div>
              </div>
              <div
                className={
                  highlightCombinedProjectAndLeaveUnderBase
                    ? `${U3129_HEADER_NUMERIC_FIELD_WRAP} [&_.header-honorarium-sum-display]:bg-red-100! [&_.header-honorarium-sum-display]:border-red-400! [&_.header-honorarium-sum-display]:ring-1 [&_.header-honorarium-sum-display]:ring-red-300`
                    : U3129_HEADER_NUMERIC_FIELD_WRAP
                }
              >
                <div className="space-y-1.5">
                  <U2000Label label={t("lblHeaderTotalHonorariumHours", { ns: NS, defaultValue: "Total honoraires" })} required={false} htmlFor={headerHonorariumSumId} />
                  <div
                    id={headerHonorariumSumId}
                    className="header-honorarium-sum-display w-full rounded-lg border border-gray-200 bg-muted px-4 py-3 text-sm tabular-nums text-gray-500"
                    aria-readonly="true"
                  >
                    {formatCurrency(combinedHonorariumHours, HEADER_HONORARIUM_SUM_DISPLAY_FORMAT)}
                  </div>
                </div>
              </div>
              {!hideExpenseAndEquipmentTabs && (
                <>
                  <div className={U3129_HEADER_NUMERIC_FIELD_WRAP}>
                    <U2000InputCurrency
                      formStateHandlers={formStateHandlers}
                      fieldName="totalExpenses"
                      label={t("lblTotalExpenses", { ns: NS, defaultValue: "Total dépenses" })}
                      readonly
                      allowNegative={false}
                      decimalDigits={2}
                    />
                  </div>
                  <div className={U3129_HEADER_NUMERIC_FIELD_WRAP}>
                    <U2000InputCurrency
                      formStateHandlers={formStateHandlers}
                      fieldName="totalEquipment"
                      label={t("lblTotalEquipment", { ns: NS, defaultValue: "Total équipement" })}
                      readonly
                      allowNegative={false}
                      decimalDigits={2}
                    />
                  </div>
                </>
              )}
            </div>
            {showPayrollHoursBlock && (
              <>
                <div className="hidden shrink-0 xl:block xl:w-px xl:self-stretch xl:bg-gray-200" aria-hidden />
                <div
                  className="flex w-max min-w-0 shrink-0 flex-nowrap gap-2 rounded-lg border border-amber-100 bg-amber-50/90 px-3 py-1 xl:flex-none"
                  role="group"
                  aria-label={t("lblHeaderPayrollHoursGroup", { ns: NS })}
                >
                  <div className={U3129_HEADER_NUMERIC_FIELD_WRAP}>
                    <U2000InputDecimal
                      formStateHandlers={formStateHandlers}
                      fieldName="approvalPaidHours"
                      label={t("lblHeaderPayrollPaid", { ns: NS })}
                      readonly
                      allowNegative={false}
                      decimalDigits={2}
                    />
                  </div>
                  <div className={U3129_HEADER_NUMERIC_FIELD_WRAP}>
                    <U2000InputDecimal
                      formStateHandlers={formStateHandlers}
                      fieldName="regularHoursToBank"
                      label={t("dlgApproveHourlySimpleHours", { ns: NS })}
                      readonly
                      allowNegative={false}
                      decimalDigits={2}
                    />
                  </div>
                  <div className={U3129_HEADER_NUMERIC_FIELD_WRAP}>
                    <U2000InputDecimal
                      formStateHandlers={formStateHandlers}
                      fieldName="halfTimeHoursToBank"
                      label={t("dlgApproveHourlyHalfHours", { ns: NS })}
                      readonly
                      allowNegative={false}
                      decimalDigits={2}
                    />
                  </div>
                  <div className={U3129_HEADER_NUMERIC_FIELD_WRAP}>
                    <U2000InputDecimal
                      formStateHandlers={formStateHandlers}
                      fieldName="doubleTimeHoursToBank"
                      label={t("dlgApproveHourlyDoubleHours", { ns: NS })}
                      readonly
                      allowNegative={false}
                      decimalDigits={2}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
      </div>
    </div>
  );
};

export default U3129TimesheetFormHeaderTab;
